import { createFileRoute, Link } from '@tanstack/react-router'
import { formatPrice } from '@hedgehog/shared'
import { BRAND_FALLBACK, usePublicConfig } from '@/components/marketing/config'
import { Brand, LegalPage, SupportEmail } from '@/components/marketing/LegalPage'

export const Route = createFileRoute('/terms')({
  head: () => ({
    meta: [
      { title: `Terms of service · ${BRAND_FALLBACK}` },
      { name: 'description', content: 'The terms for the free audit, the free preview, and the paid rebuild and hosting.' },
    ],
  }),
  component: Terms,
})

function Terms() {
  return (
    <LegalPage
      title="Terms of service"
      intro={
        <p>
          These terms cover the free audit, the free homepage preview, and the paid rebuild and hosting of your site by{' '}
          <Brand /> (“we”, “us”). They are written to be read. If anything is unclear, ask us at <SupportEmail />.
        </p>
      }
    >
      <h2 id="service">1. What we do</h2>
      <p>
        We rebuild a public WordPress website with the same design on our platform, host it, and give you an editor to
        change its text and images. The rebuild covers:
      </p>
      <ul>
        <li>pages, blog posts, categories, images and downloadable files that can be reached from the public site</li>
        <li>the header, footer, menus and page layouts, reproduced to match the original</li>
        <li>a 301 redirect for every old web address, and the page titles, meta descriptions and schema markup</li>
        <li>hosting, an SSL certificate, the editor and email support while your plan is active</li>
      </ul>
      <p>
        A site can have up to <MaxPages /> pages and 10 page types. We don’t rebuild contact forms, shops, bookings, memberships,
        logins, forums or other plugin features, multilingual sites, sites behind a login, or content that isn’t reachable from
        the public site. We don’t host email or redesign sites. The audit tells you before you pay if your site has any
        of these.
      </p>

      <h2 id="authority">2. Your right to order a rebuild</h2>
      <p>
        By requesting a preview or placing an order, you confirm that you own the website and its domain, or that you
        are authorised to act for the owner, and that you have the right to let us copy, rebuild and host its content.
      </p>
      <p>
        Going live requires a change to the domain’s DNS records. That change is how you prove you control the domain.
        If you can’t make it, we can’t put the site live, and we refund the order in full.
      </p>

      <h2 id="preview">3. The free audit and the free preview</h2>
      <p>
        The audit reads only the public pages of the site you enter. To get a preview you give us your email address
        and accept these terms. Previews are limited to protect site owners and to keep previews free:
      </p>
      <ul>
        <li>a preview has a private, unguessable address and is hidden from search engines</li>
        <li>every page shows a banner saying it is a preview, and its forms don’t send anything</li>
        <li>a preview expires after 14 days</li>
        <li>each domain can have one preview every 30 days, and each email address can request three a day</li>
      </ul>
      <p>
        The full-site preview you review after paying has the same protections until the site goes live. You must not
        use a preview to copy, publish or pass off a site you don’t own.
      </p>

      <h2 id="price">4. Price and payment</h2>
      <p>
        You pay once for the rebuild and the first year of hosting, the SSL certificate, the editor and email support.
        The price depends on the number of pages on your site. It is shown in your audit and at checkout before you
        pay. The first year starts when your site goes live. We do not add sales tax or VAT at checkout.
      </p>
      <p>
        After the first year, hosting renews once a year. <RenewalPrice /> We email you 30 days before each renewal
        with the renewal date and price.
      </p>
      <p>
        Payments are handled by Stripe. We never see or store your full card details. Your card is saved for the yearly
        renewal. You can update your card, download invoices and cancel the renewal from your account.
      </p>

      <h2 id="approval">5. Reviewing and approving your site</h2>
      <p>
        When the full site is ready, you review it page by page. You can send notes up to two times; each submission is
        one round of changes. We apply your notes and check the site again.
      </p>
      <p>
        The goal is the same design on a better platform. Notes asking for new pages, new features or a redesign are
        outside the rebuild, and we tell you when a note falls into that group.
      </p>

      <h2 id="go-live">6. Going live</h2>
      <p>
        After you approve the site, we give you instructions to change the DNS record for your domain at your provider.
        We never ask you to change your mail records, and we check them before and after the switch. Keep your old
        hosting for 30 days after go-live as a fallback; we remind you when it is safe to cancel it.
      </p>

      <h2 id="refunds">7. Refunds</h2>
      <p>You get a full refund of everything you paid for the order if:</p>
      <ul>
        <li>after the two included rounds of changes, you reject the site, or</li>
        <li>your site is not live within 30 days of payment and you ask for a refund, or</li>
        <li>you can’t prove control of the domain, as described in section 2.</li>
      </ul>
      <p>
        To ask for a refund, email <SupportEmail /> from the address you ordered with. Refunds go back to the card you
        paid with.
      </p>

      <h2 id="cancellation">8. Cancelling</h2>
      <p>You can cancel the yearly renewal at any time from your account. When you cancel:</p>
      <ul>
        <li>we send you an export of your content and a static copy of your site that you can host anywhere</li>
        <li>
          your site stays online until the end of the year you paid for, and for at least 30 days, so you have time to
          move it
        </li>
        <li>no further yearly fees are charged</li>
      </ul>
      <p>There is no lock-in. Your domain stays registered with your own provider throughout.</p>

      <h2 id="failed-payments">9. Failed payments</h2>
      <p>
        If a yearly payment fails, Stripe retries it and we email you. If the retries also fail, your site stays
        online for 30 more days. After that, visitors see a holding page instead of your site. We keep your content for
        90 days, so you can pay and restore the site or ask for an export. After 90 days we delete it.
      </p>

      <h2 id="content">10. Your content and fonts</h2>
      <p>
        Your text, images, files and domain remain yours. You give us permission to copy, process, store and publish
        them only to provide the service to you.
      </p>
      <p>
        You are responsible for having the rights to the content on your site, including photos and fonts. Some
        commercial fonts are licensed for one website or one host only. If your current site uses such a font, it is
        your responsibility to make sure the licence covers the rebuilt site. Where a font’s licence is unclear, we may
        use a close free alternative instead.
      </p>

      <h2 id="use">11. Acceptable use</h2>
      <p>
        You must not use the service to publish unlawful content, to impersonate another business, or to copy a site
        you are not authorised to act for. We may suspend a site that breaks these rules and will tell you why.
      </p>

      <h2 id="ai">12. How we build sites</h2>
      <p>
        AI models do most of the rebuilding, and a separate model reviews the result before you see it. They only
        receive your public site content. Details are in our <Link to="/privacy">privacy policy</Link> and on our{' '}
        <Link to="/subprocessors">list of subprocessors</Link>.
      </p>

      <h2 id="liability">13. Service and liability</h2>
      <p>
        We work to keep your site online and correct, but we can’t promise the service will never be interrupted. To
        the extent the law allows, our total liability to you for any claim is limited to the amount you paid us in the
        12 months before the claim. Nothing in these terms limits liability that cannot be limited by law, or your
        rights as a consumer where those apply.
      </p>

      <h2 id="changes">14. Changes to these terms</h2>
      <p>
        If we make a change that affects active customers, we email you at least 30 days before it takes effect. If you
        don’t agree, you can cancel under section 8.
      </p>

      <h2 id="contact">15. Contact</h2>
      <p>
        Questions about these terms: <SupportEmail />.
      </p>
    </LegalPage>
  )
}

/** Page limit of the last price tier, with the current value as the fallback while the config loads. */
function MaxPages() {
  const { config } = usePublicConfig()
  const tiers = config?.priceTiers ?? []
  return <>{tiers.length ? tiers[tiers.length - 1].maxPages : 100}</>
}

/** RENEWAL_YEARLY_CENTS from config; empty until the price is set. */
function RenewalPrice() {
  const { config } = usePublicConfig()
  if (config?.renewalSameAsPrice) return <>Each year costs the same as your first payment.</>
  if (config?.renewalYearlyCents == null) return <>The renewal price is shown before your first year ends.</>
  return <>It costs {formatPrice(config.renewalYearlyCents, config.currency)} a year.</>
}
